import React, { useMemo, useState } from 'react';
import { UserPlus, Mail, X, AlertCircle } from 'lucide-react';

/**
 * Lets the agent pick which attendees get a buyer-portal sign-up link.
 * Mirrors SendContractModal — same picker pattern, different action.
 */
export default function InvitePortalModal({ attendees = [], sending, onClose, onSend }) {
  const emailable = useMemo(
    () => attendees.filter((a) => (a.email || '').trim()),
    [attendees],
  );
  const [selected, setSelected] = useState(() => emailable.map((a) => a.id));

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const allSelected = emailable.length > 0 && selected.length === emailable.length;
  const toggleAll = () => setSelected(allSelected ? [] : emailable.map((a) => a.id));

  const handleSend = () => {
    if (!selected.length) return;
    onSend(selected);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-[#4EADB7]" />
            <div>
              <h2 className="text-base font-semibold text-gray-900">Invite to Portal</h2>
              <p className="text-xs text-gray-500 mt-0.5">Choose who receives a buyer-portal sign-up link by email.</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-4">
          {attendees.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">No attendees have checked in yet.</p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">
                  Attendees ({emailable.length} emailable)
                </span>
                {emailable.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="text-xs text-[#4EADB7] hover:underline"
                  >
                    {allSelected ? 'Clear all' : 'Select all'}
                  </button>
                )}
              </div>

              <div className="max-h-72 overflow-y-auto divide-y border rounded-lg">
                {attendees.map((a) => {
                  const email = (a.email || '').trim();
                  const disabled = !email;
                  const checked = selected.includes(a.id);
                  return (
                    <label
                      key={a.id}
                      className={`flex items-center gap-3 px-3 py-2.5 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}
                    >
                      <input
                        type="checkbox"
                        className="accent-[#4EADB7]"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => !disabled && toggle(a.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {a.full_name || a.name || 'Unnamed buyer'}
                        </div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-1 truncate">
                          {email ? (
                            <><Mail className="w-3 h-3 shrink-0" /> {email}</>
                          ) : (
                            <span className="flex items-center gap-1 text-amber-600">
                              <AlertCircle className="w-3 h-3 shrink-0" /> No email on file
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t bg-slate-50 rounded-b-2xl">
          <span className="text-xs text-slate-500">{selected.length} selected</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-xs px-3 py-2 border rounded-md bg-white hover:bg-slate-50 text-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !selected.length}
              className="text-xs flex items-center gap-1 px-3 py-2 bg-[#4EADB7] hover:bg-[#3f9ba4] disabled:opacity-50 text-white rounded-md"
            >
              <UserPlus className="w-3.5 h-3.5" />
              {sending ? 'Sending…' : `Send to ${selected.length} buyer${selected.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
